import {
  getBeeswaxThirdPartyDataProvider,
  getBeeswaxThirdPartySegmentSearch,
  getBeeswaxThirdPartySegmentTree,
} from "@/apis/v1/beeswax";
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { MutatedDataProvider } from "../../../targetingExpression.interface";
import { useForm } from "react-hook-form";
import { DataProviderList } from "./segment.interface";

const ALLOWED_DATA_PROVIDERS_BUZZ_KEY = ["ias", "dv"] // NOTE: Integral Ad Sciences(ias) and DoubleVerify(dv)

export const segmentKeys = {
  default: () => ["third-party", "segment"],
  dataProvider: () => [...segmentKeys.default(), "data-provider"],
  search: ({
    data_provider_id,
    search,
    ...rest
  }: {
    data_provider_id: number;
    search: string;
  }) => [
      ...segmentKeys.default(),
      "search",
      data_provider_id,
      search,
      { ...rest },
    ],
  tree: (params: { key?: string;[key: string]: any } = {}) => [...segmentKeys.default(), "tree", params.key || 'default'],
  multiKeyTree: () => [...segmentKeys.default(), "tree", "multiple"],
};
interface SegmentFormData {
  selected: any[];
}

export function useSegmentForm({
  data,
}: {
  data?: any[] | null;
} = {}) {
  const rhForm = useForm<SegmentFormData>({
    defaultValues: {
      selected: data ?? [],
    },
  });

  return rhForm;
}
export function useDataProvider(params?: {
  queryParams?: {
    id?: number;
    name?: string;
    open_rtb_code?: string;
  };
  options?: Record<string, any>;
}) {
  const { queryParams = {}, options = {} } = params ?? {};
  return useQuery<DataProviderList>(
    segmentKeys.dataProvider(),
    getBeeswaxThirdPartyDataProvider,
    {
      meta: {
        queryParams: {
          ...queryParams,
        },
      },
      ...options,
    }
  );
}

export function useSegmentSearch(params: {
  queryParams: {
    page: number;
    page_size: number;
    search: string;
    data_provider_id: number;
  };
  options?: Record<string, any>;
}) {
  const { queryParams, options = {} } = params ?? {};
  return useQuery(
    segmentKeys.search(queryParams),
    getBeeswaxThirdPartySegmentSearch,
    {
      meta: {
        queryParams: {
          ...queryParams,
        },
      },
      ...options,
    }
  );
}

interface SegmentTreeParams {
  queryParams?: {
    type?: string;
    key__in?: string[];
    page_size?: number;
    [key: string]: any;
  };
  options?: Record<string, any>;
}

export function useSegmentTree(params?: SegmentTreeParams) {
  const { queryParams = {}, options = {} } = params ?? {};
  return useQuery(
    segmentKeys.tree(queryParams),
    getBeeswaxThirdPartySegmentTree,
    {
      meta: {
        queryParams: {
          ...queryParams,
        },
      },
      ...options,
    }
  );
}

interface SegmentDataParams {
  data?: any;
  isEdit?: boolean;
}

export function useSegmentData({ data }: SegmentDataParams) {
  const [mutatedDataProvider, setMutatedDataProvider] = useState<
    MutatedDataProvider[]
  >([]);

  const initialData = mapSelectedData(data) ?? [];
  const [treeData, setTreeData] = useState<MutatedDataProvider[]>([]);
  // state for loaded nodes
  const [nodes, setNodes] = useState<Record<string, any>>({});
  const [allNodes, setAllNodes] = useState<Record<string, any>>({});
  const [valuesForSearchList, setValuesForSearchList] =
    useState<Array<Record<string, any>>>();
  const [dataProvidersLoaded, setDataProvidersLoaded] = useState(false);

  // Initialize treeData with mutatedDataProvider when available
  useEffect(() => {
    if (mutatedDataProvider.length > 0 && treeData.length === 0) {
      setTreeData(mutatedDataProvider);
    }
  }, [mutatedDataProvider, treeData.length]);

  let nodesToBeFetched: string[] = [];
  if (initialData?.length) {
    nodesToBeFetched = initialData.filter((item) => {
      return !allNodes[item];
    });
  }
  const { isLoading: dataProviderLoading, isFetching: isDataProviderFetching, refetch: refetchDataProvider } = useDataProvider({
    options: {
      onSuccess: (data: DataProviderList) => {
        const filteredDataProviderList = filterDataProviders(data)
        const mappedData: MutatedDataProvider[] = filteredDataProviderList.map((item) => ({
          name: item.name,
          id: item.buzz_key,
          children: [] as [],
          isChildren: Boolean(item.children_url),
          isBranch: true,
          parent: "root",
          accountId: item.account_id,
        }));
        const mappedDataForSearchList = filteredDataProviderList.map((item) => ({
          name: item.name,
          id: item.buzz_key,
          children: [],
          dataProviderId: item.account_id,
        }));
        const keys = mappedData.map((item) => item.id);
        const rootNode: MutatedDataProvider = {
          name: "",
          id: "root",
          children: keys as [],
          parent: null,
          isChildren: true,
          isBranch: true,
        };

        setMutatedDataProvider(() => {
          return [rootNode, ...mappedData];
        });
        setTreeData([rootNode, ...mappedData]);
        setValuesForSearchList(mappedDataForSearchList);
        setDataProvidersLoaded(true);
      },
    },
  });
  const { isLoading: segmentsLoading } =
    useSegmentTree({
      queryParams: {
        type: "segment",
        key__in: nodesToBeFetched,
        page_size: 500,
      },
      options: {
        enabled: Boolean(nodesToBeFetched.length),
        onSuccess: (data: any) => {
          if (data?.results?.length) {
            const tempObj: Record<string, any> = {};
            data.results.forEach((item: any) => {
              tempObj[item.key] = {
                name: item.name,
                id: item.key,
                children: [] as [],
                isChildren: Boolean(item.children_url),
                isBranch: Boolean(item.children_url),
                parent: item.parent_key,
                dataProviderId: item.data_provider_id,
                verb_usage: item.verb_usage,
              };
            });
            setAllNodes((current) => ({ ...current, ...tempObj }));
          }
        },
      },
    });

  // Initialize data providers on mount or when data providers haven't been loaded
  useEffect(() => {
    // Refetch data providers if they haven't been loaded
    // This ensures data is available when switching tabs
    if (!dataProvidersLoaded && refetchDataProvider) {
      refetchDataProvider();
    }
  }, [dataProvidersLoaded, refetchDataProvider]); // Re-run when dataProvidersLoaded changes

  return {
    nodes,
    setNodes,
    treeData,
    setTreeData,
    allNodes,
    setAllNodes,
    initialData,
    valuesForSearchList,
    setValuesForSearchList,
    isLoading: (!dataProvidersLoaded && dataProviderLoading) || (segmentsLoading && nodesToBeFetched.length > 0) || (treeData.length === 0 && dataProviderLoading),
    isDataProviderFetching,
    isLoadingSegments: segmentsLoading,
    isLoadingDataProvider: dataProviderLoading,
  };
}

interface SelectedData {
  value: {
    excluded: Map<string, string>;
    included: Map<string, string>;
    required: Map<string, string>;
  };
}

function mapSelectedData(data: SelectedData | null | undefined) {
  const array: string[] = [];
  if (data) {
    array.push(...mapToArray(data.value.excluded));
    array.push(...mapToArray(data.value.included));
    array.push(...mapToArray(data.value.required));
  }
  return array;
}

function mapToArray(data: Map<string, string>): string[] {
  return Array.from(data.keys());
}

export function filterDataProviders(dataProviderList: DataProviderList | undefined) {
  if (dataProviderList) {
    return dataProviderList.results.filter((dProvider) => ALLOWED_DATA_PROVIDERS_BUZZ_KEY.includes(dProvider.buzz_key))
  }
  return []
}
